import React, {
  useState,
  useContext,
  useCallback,
  useEffect,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import format from 'string-template';
import * as Sentry from '@sentry/browser';
import { v4 as uuidv4 } from 'uuid';

import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Avatar,
} from '@mui/material';
import { PostAdd } from '@mui/icons-material';
import {
  DatePicker,
  TimePicker,
} from '@mui/lab';

import { combineDateAndTime } from '../../../../utils/date';
import { storagePaths, pathPlaceholder } from '../../../../utils/firebasePaths';
import { uploadMessageAttachments } from '../../../../utils/attachments';
import { MAX_FILE_UPLOAD_SIZE, MAX_FILE_UPLOAD_SIZE_MB } from '../../../../utils/files';
import { toDisplayableFile } from '../../../../utils/dataProcessing';
import useStorage from '../../../../hooks/useStorage';
import UserContext from '../../../../context/UserContext';
import LoggedInUserContext from '../../../../context/LoggedInUserContext';
import FileSelectButton from '../../../../components/FileSelectButton';
import AttachmentsPreview from '../../../../chat/components/ChannelMessageInput/components/AttachmentsPreview';
import CoachTask, {
  CoachTaskPriority,
  NO_CLIENT_TASK_TYPES,
  CLIENT_DIRECTED_TASK_TYPES,
} from '../../../Model/CoachTask';

import DialogRoundedModal, { ModalBody, ModalActionButton } from '../../../../components/DialogRoundedModal';
import {
  FormFields,
  FieldRow,
  SubjectRow,
  StyledTextField,
  StyledTextarea,
  AttachButton,
  AttachmentsPreviewWrapper,
} from './styles';
import text from './text.json';

// Default deadline: tomorrow, at the current time. Factories (not constants) so each
// call gets a fresh moment — shared by the initial state and resetFields.
const getDefaultDeadlineDate = () => moment().local().add(1, 'day');
const getDefaultDeadlineTime = () => moment().local();

const CreateCoachTaskModal = ({
  isOpen,
  onDidDismiss,
  onCreated,
  onUpdated,
  onError,
  task,
  coachId: coachIdProp,
  coachName: coachNameProp,
  coachAvatar: coachAvatarProp,
  subjectId,
  subjectName,
  subjectAvatar,
  channelId,
  messageId,
  messagePreview,
  ...modalProps
}) => {
  // UserContext is only present on a coach's dashboard (the header entry); the admin
  // page has no provider, so it defaults to null. Coach data is passed as props there.
  const { userId: ctxCoachId, userDoc: ctxCoachDoc } = useContext(UserContext) || {};
  const { userId: loggedInUserId, userDoc: loggedInUserDoc } = useContext(LoggedInUserContext);
  const { uploadAttachments, deleteAttachments, getBlobUrl } = useStorage();

  const isEditMode = !!task;
  const coachId = coachIdProp || ctxCoachId;
  const coachName = coachNameProp || ctxCoachDoc?.name;
  const coachAvatar = coachAvatarProp || ctxCoachDoc?.avatarUrl;

  // The task's subject. When a client is attached, the task is client-directed
  // (user = the client) and offers the client deliverable types; with no client the
  // subject is the coach (user = coach, like CLIENT_STATUS) and only the client-less
  // types apply. Removing the client (create mode) un-ties the task to the coach.
  const [subject, setSubject] = useState(null);
  const allowedTaskTypes = useMemo(
    () => (subject ? CLIENT_DIRECTED_TASK_TYPES : NO_CLIENT_TASK_TYPES),
    [subject],
  );

  const [taskTitle, setTaskTitle] = useState('');
  const [taskType, setTaskType] = useState(NO_CLIENT_TASK_TYPES[0]);
  const [priority, setPriority] = useState(CoachTaskPriority.MEDIUM);
  const [deadlineDate, setDeadlineDate] = useState(getDefaultDeadlineDate);
  const [deadlineTime, setDeadlineTime] = useState(getDefaultDeadlineTime);
  const [instructions, setInstructions] = useState('');
  // Each item is { id, url (preview), file }. Existing (edit) attachments also carry
  // `attachmentData` (the stored object) so they're preserved on save, not re-uploaded.
  const [attachments, setAttachments] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetFields = useCallback((nextSubject) => {
    const types = nextSubject ? CLIENT_DIRECTED_TASK_TYPES : NO_CLIENT_TASK_TYPES;
    setTaskTitle('');
    setTaskType(types[0]);
    setPriority(CoachTaskPriority.MEDIUM);
    setDeadlineDate(getDefaultDeadlineDate());
    setDeadlineTime(getDefaultDeadlineTime());
    setInstructions('');
    setAttachments([]);
  }, []);

  // Drop the client (create mode only); keep the type if it's still valid for a
  // client-less task (SOCIAL_MEDIA_POST), otherwise fall back to GENERIC.
  const handleRemoveSubject = useCallback(() => {
    setSubject(null);
    setTaskType((prev) => (NO_CLIENT_TASK_TYPES.includes(prev) ? prev : NO_CLIENT_TASK_TYPES[0]));
  }, []);

  // Prefill from the task when opening in edit mode; reset to create defaults otherwise.
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    if (!task) {
      // Seed the subject from the client prop (a client-header entry); no prop → coach-level.
      const initialSubject = subjectId
        ? { id: subjectId, name: subjectName, avatar: subjectAvatar }
        : null;
      setSubject(initialSubject);
      resetFields(initialSubject);
      return undefined;
    }
    let isMounted = true;
    // A persisted task is client-directed when its subject (user) isn't the coach.
    const taskSubject = task.user && task.user !== task.coach
      ? { id: task.user, name: task.userName, avatar: task.userAvatar }
      : null;
    setSubject(taskSubject);
    setTaskTitle(task.taskTitle || '');
    setTaskType(task.taskType || (taskSubject ? CLIENT_DIRECTED_TASK_TYPES[0] : NO_CLIENT_TASK_TYPES[0]));
    setPriority(task.priority || CoachTaskPriority.MEDIUM);
    setDeadlineDate(task.deadline ? moment(task.deadline) : getDefaultDeadlineDate());
    setDeadlineTime(task.deadline ? moment(task.deadline) : getDefaultDeadlineTime());
    setInstructions(task.instructions || '');
    setAttachments([]);
    // Resolve existing attachments to preview URLs; keep the stored object so unchanged
    // files aren't re-uploaded on save. Resolve each independently so one failed blob
    // (deleted/transient error) degrades to a missing preview without rejecting the
    // batch or dropping the other attachments.
    (async () => {
      const existing = await Promise.all((task.attachments || []).map(async (attachment) => {
        let url = '';
        try {
          url = await getBlobUrl(attachment.assetPath);
        } catch (error) {
          Sentry.captureException(error, { extra: { assetPath: attachment.assetPath } });
        }
        return {
          id: uuidv4(),
          url,
          file: { name: attachment.title, type: attachment.mimeType, size: attachment.fileSize },
          attachmentData: attachment,
        };
      }));
      if (isMounted) {
        setAttachments(existing);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [isOpen, task, subjectId, subjectName, subjectAvatar, resetFields, getBlobUrl]);

  const handleAddFiles = useCallback(async (fileList) => {
    const files = Object.values(fileList);
    if (!files.length) {
      return;
    }
    if (!files.every((file) => file.size < MAX_FILE_UPLOAD_SIZE)) {
      onError(format(text.fileTooLarge, { maxFileSize: MAX_FILE_UPLOAD_SIZE_MB }));
      return;
    }
    // AttachmentsPreview draws the picked file, and a browser that cannot decode HEIC
    // draws nothing. Replacing the File rather than just the preview url keeps the name
    // in step, since the upload reads its extension.
    const displayableFiles = await Promise.all(files.map(async (file) => {
      const displayableFile = await toDisplayableFile(file);
      return { id: uuidv4(), url: URL.createObjectURL(displayableFile), file: displayableFile };
    }));
    setAttachments((prev) => [...prev, ...displayableFiles]);
  }, [onError]);

  const handleRemoveAttachment = useCallback((attachmentId) => {
    setAttachments((prev) => prev.filter((item) => item.id !== attachmentId));
  }, []);

  // taskType and priority are controlled selects with non-null defaults; only the
  // title and the (clearable) deadline pickers need validating.
  const isValid = !!taskTitle.trim() && !!deadlineDate && !!deadlineTime;

  const handleSubmit = useCallback(async () => {
    setIsSubmitting(true);
    try {
      const deadline = combineDateAndTime(deadlineDate, deadlineTime).toDate();

      // Existing attachments keep their stored object; newly picked files are uploaded.
      // Both end up as the shared object shape ({ type, title, assetPath, fileSize,
      // mimeType }) so file name/type aren't parsed back out of the URL.
      const keptAttachments = attachments
        .filter((item) => item.attachmentData)
        .map((item) => item.attachmentData);
      const newFiles = attachments
        .filter((item) => !item.attachmentData)
        .map((item) => item.file);

      let uploadedAttachments = [];
      if (newFiles.length) {
        const storagePath = format(storagePaths.COACH_TASK_ATTACHMENTS_BY_COACH, {
          [pathPlaceholder.COACH_ID]: coachId,
        });
        const { attachmentsData, storageTaskFailed } = await uploadMessageAttachments({
          fileAttachments: newFiles,
          userId: loggedInUserId,
          uploadAttachments,
          deleteAttachments,
          storagePath,
        });
        if (storageTaskFailed) {
          onError(text.uploadError);
          return;
        }
        uploadedAttachments = attachmentsData;
      }
      const finalAttachments = [...keptAttachments, ...uploadedAttachments];

      // Edit: update the editable fields in place; coach / assignedBy / type ownership
      // never change. Removed attachments are dropped from the doc (storage cleanup is
      // a follow-up — orphaned blobs are harmless).
      if (isEditMode) {
        await task.updateDetails({
          taskType,
          taskTitle: taskTitle.trim(),
          priority,
          deadline,
          instructions: instructions.trim(),
          attachments: finalAttachments,
          updatedBy: loggedInUserId,
        });
        onUpdated();
        onDidDismiss();
        return;
      }

      // `user` is the task's subject. With a client attached the subject is the client
      // (client-directed, user !== coach); with no client it's the coach (like
      // CLIENT_STATUS) so the feed header stays coach-tied and the `docs[0].user` guard
      // holds. `coach` is always the assigned coach (drives the feed/admin query).
      const userName = subject ? subject.name : coachName;
      const userAvatar = subject ? subject.avatar : coachAvatar;
      const taskData = {
        coach: coachId,
        user: subject ? subject.id : coachId,
        assignedBy: loggedInUserId,
        taskType,
        taskTitle: taskTitle.trim(),
        priority,
        deadline,
        attachments: finalAttachments,
      };
      // Firestore rejects undefined values — only set optional fields when present.
      if (loggedInUserDoc?.name) {
        taskData.assignedByName = loggedInUserDoc.name;
      }
      if (instructions.trim()) {
        taskData.instructions = instructions.trim();
      }
      // Subject display data for the feed header (UserProfileCard reads these).
      if (userName) {
        taskData.userName = userName;
      }
      if (userAvatar) {
        taskData.userAvatar = userAvatar;
      }
      // Link to the chat message this task was created from, so the coach can reply
      // to it in context. messagePreview is denormalized so task surfaces render it
      // without fetching the message.
      if (channelId) {
        taskData.channelId = channelId;
      }
      if (messageId) {
        taskData.messageId = messageId;
      }
      if (messagePreview) {
        taskData.messagePreview = messagePreview;
      }
      await CoachTask.create(taskData);
      resetFields(subject);
      onCreated();
      onDidDismiss();
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          coachId,
          loggedInUserId,
          taskId: task?.id,
        },
      });
      onError();
    } finally {
      setIsSubmitting(false);
    }
  }, [
    isEditMode,
    task,
    attachments,
    subject,
    coachId,
    coachName,
    coachAvatar,
    loggedInUserId,
    loggedInUserDoc,
    taskType,
    taskTitle,
    priority,
    deadlineDate,
    deadlineTime,
    instructions,
    channelId,
    messageId,
    messagePreview,
    uploadAttachments,
    deleteAttachments,
    resetFields,
    onCreated,
    onUpdated,
    onError,
    onDidDismiss,
  ]);

  return (
    <DialogRoundedModal
      open={isOpen}
      onClose={onDidDismiss}
      title={isEditMode ? text.editTitle : text.title}
      description={text.subtitle}
      maxWidth="sm"
      fullWidth
      actionButtons={(
        <ModalActionButton
          onClick={handleSubmit}
          disabled={!isValid || isSubmitting}
        >
          {isEditMode ? text.saveTask : text.createTask}
        </ModalActionButton>
      )}
      {...modalProps}
    >
      <ModalBody>
        <FormFields>
          {subject && (
            <SubjectRow>
              <Chip
                avatar={<Avatar src={subject.avatar} alt={subject.name} />}
                label={subject.name}
                onDelete={isEditMode ? undefined : handleRemoveSubject}
                variant="outlined"
              />
            </SubjectRow>
          )}
          <StyledTextField
            label={text.taskTitleLabel}
            placeholder={text.taskTitlePlaceholder}
            value={taskTitle}
            onChange={(event) => setTaskTitle(event.target.value)}
            required
          />
          <FieldRow>
            <FormControl fullWidth>
              <InputLabel id="coach-task-category-label">{text.categoryLabel}</InputLabel>
              <Select
                labelId="coach-task-category-label"
                label={text.categoryLabel}
                value={taskType}
                onChange={(event) => setTaskType(event.target.value)}
              >
                {allowedTaskTypes.map((type) => (
                  <MenuItem key={type} value={type}>
                    {text.taskTypeLabels[type]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel id="coach-task-priority-label">{text.priorityLabel}</InputLabel>
              <Select
                labelId="coach-task-priority-label"
                label={text.priorityLabel}
                value={priority}
                onChange={(event) => setPriority(event.target.value)}
              >
                {Object.values(CoachTaskPriority).map((priorityValue) => (
                  <MenuItem key={priorityValue} value={priorityValue}>
                    {text.priorityLabels[priorityValue]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </FieldRow>
          <FieldRow>
            <DatePicker
              label={text.deadlineDateLabel}
              value={deadlineDate}
              minDate={moment().local()}
              onChange={(newValue) => setDeadlineDate(newValue)}
              renderInput={(params) => <StyledTextField {...params} />}
            />
            <TimePicker
              label={text.deadlineTimeLabel}
              value={deadlineTime}
              onChange={(newValue) => setDeadlineTime(newValue)}
              minTime={deadlineDate && moment(deadlineDate).isSame(moment().local(), 'day') ? moment().local() : null}
              minutesStep={5}
              renderInput={(params) => <StyledTextField {...params} />}
            />
          </FieldRow>
          <StyledTextarea
            placeholder={text.instructionsPlaceholder}
            value={instructions}
            rows={4}
            onIonChange={(event) => setInstructions(event.detail.value || '')}
          />
          <FileSelectButton
            ButtonComponent={AttachButton}
            onSelect={(event) => handleAddFiles(event.target.files)}
            multiple
            disabled={isSubmitting}
            extraButtonProps={{ variant: 'contained', startIcon: <PostAdd /> }}
          >
            {text.attachFiles}
          </FileSelectButton>
          {attachments.length > 0 && (
            <AttachmentsPreviewWrapper>
              <AttachmentsPreview
                attachments={attachments}
                onRemove={handleRemoveAttachment}
                onAddAttachments={handleAddFiles}
              />
            </AttachmentsPreviewWrapper>
          )}
        </FormFields>
      </ModalBody>
    </DialogRoundedModal>
  );
};

CreateCoachTaskModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onDidDismiss: PropTypes.func.isRequired,
  onCreated: PropTypes.func.isRequired,
  onUpdated: PropTypes.func,
  onError: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  task: PropTypes.object,
  coachId: PropTypes.string,
  coachName: PropTypes.string,
  coachAvatar: PropTypes.string,
  // The client this task is for (a client-header entry). Absent → coach-level task.
  // The offered task types follow whether a client is attached.
  subjectId: PropTypes.string,
  subjectName: PropTypes.string,
  subjectAvatar: PropTypes.string,
  // Chat-message link (created from a chat message). All optional — present only for
  // message-sourced tasks; written to the task doc so the coach can reply in context.
  channelId: PropTypes.string,
  messageId: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  messagePreview: PropTypes.object,
};

CreateCoachTaskModal.defaultProps = {
  onUpdated: () => {},
  task: null,
  coachId: '',
  coachName: '',
  coachAvatar: '',
  subjectId: '',
  subjectName: '',
  subjectAvatar: '',
  channelId: '',
  messageId: '',
  messagePreview: null,
};

export default CreateCoachTaskModal;
